import { getCategory } from '../category';
import { getAnonymousId } from '../settings';
import { getCities, getStateCode, getTopCities } from '../states';
import { getVisitorId } from '../visitor';

/** @internal */
export const RECOMMENDATIONS_LOADED =
	'@fe-marketplace/statePage/recommendations_loaded';

export const TOP_CITY_VENDOR_LIMIT = 4;
export const ADDITIONAL_CITY_VENDOR_LIMIT = 2;

/** @internal */
export const initialState = {
	recommendedVendorsByCity: {},
};

export default function reducer(state = initialState, action) {
	switch (action.type) {
		case RECOMMENDATIONS_LOADED: {
			const { city, profiles, total, isPermitted, stateCode } = action.payload;

			return {
				...state,
				recommendedVendorsByCity: {
					...state.recommendedVendorsByCity,
					[city]: {
						profiles: profiles || [],
						total,
						isPermitted,
						stateCode,
					},
				},
			};
		}
		default:
			return state;
	}
}

/** @internal */
export function fetchRecommendationsByCity(methods, city, limit = 4) {
	return async (dispatch, getState) => {
		const state = getState();
		const category = getCategory(state);
		const stateCode = getStateCode(state);
		const visitorId = getVisitorId(state);
		const memberId = state.membership?.member?.id;
		const anonymousId = getAnonymousId(state);

		const member =
			memberId || anonymousId
				? {
						userId: memberId || undefined,
						anonymousId: anonymousId || undefined,
					}
				: undefined;

		const { experiments } = state;
		const response = await methods.recommendedSearch({
			categoryGuid: category.id,
			city,
			state: stateCode,
			limit,
			visitorId,
			experiments,
			...(member && { member }),
		});

		const isPermitted = await methods.isOnSeoPermitted({
			categoryGuid: category.id,
			city,
			state: stateCode,
		});

		return dispatch({
			type: RECOMMENDATIONS_LOADED,
			payload: {
				city,
				profiles: response?.data?.search?.profiles,
				total: response?.data?.search?.total?.profiles,
				isPermitted,
				stateCode,
			},
		});
	};
}

export function fetchAllRecommendations(methods) {
	return async (dispatch, getState) => {
		const state = getState();
		const topCities = getTopCities(state);
		const cities = getCities(state);

		const topCitiesRecommendations = topCities.map((city) =>
			dispatch(
				fetchRecommendationsByCity(methods, city, TOP_CITY_VENDOR_LIMIT),
			),
		);

		const citiesRecommendations = cities.map((city) =>
			dispatch(
				fetchRecommendationsByCity(methods, city, ADDITIONAL_CITY_VENDOR_LIMIT),
			),
		);

		return Promise.all([...topCitiesRecommendations, ...citiesRecommendations]);
	};
}

export const getRecommendedVendorProfilesByCity = (state, city) => {
	const recommendation = state?.statePage?.recommendedVendorsByCity[city];
	return recommendation &&
		(recommendation.stateCode === undefined ||
			recommendation.stateCode === state?.states?.code)
		? recommendation.profiles || []
		: [];
};

export const getRecommendedVendorTotalByCity = (state, city) => {
	const recommendation = state?.statePage?.recommendedVendorsByCity[city];
	return recommendation &&
		(recommendation.stateCode === undefined ||
			recommendation.stateCode === state?.states?.code)
		? recommendation.total || 0
		: 0;
};

export const isRecommendationPermittedByCity = (state, city) =>
	state?.statePage?.recommendedVendorsByCity[city]?.isPermitted || true;

export const getVisibleRecommendationModules = (state) => {
	const stateCode = getStateCode(state);
	const byCity = state?.statePage?.recommendedVendorsByCity || {};
	const makeModules = (cities, limit) =>
		cities.flatMap((city) => {
			const recommendation = byCity[city];
			if (
				(recommendation?.stateCode !== undefined &&
					recommendation.stateCode !== stateCode) ||
				!Array.isArray(recommendation?.profiles) ||
				recommendation.profiles.length < limit
			) {
				return [];
			}

			return [{ city, vendors: recommendation.profiles.slice(0, limit) }];
		});

	return [
		...makeModules(getTopCities(state) || [], TOP_CITY_VENDOR_LIMIT),
		...makeModules(getCities(state) || [], ADDITIONAL_CITY_VENDOR_LIMIT),
	];
};
